import React from "react";
import { type NextPage } from "next";
import { getLandingPageProps, getPageMetaImage, type LandingPageProps } from "utils";
import { Page } from "components/page";
import {
  WarehouseHero,
  WarehouseAsCdp,
  WarehouseCdpDiagram,
  WarehouseAccordion,
  WarehouseSecurityFeatures,
  WarehouseWordCloud,
  WarehouseUseCaseTabs,
  WarehouseFaq,
  WarehouseCustomers,
} from "components/solutions/warehouses";
import { DataTeamsCards } from "components/platform-overview-content";
import { WarehouseCdpProvider, WarehouseCdpVariants } from "contexts/warehouse-cdp-context";
import { SolidPlate } from "ui/plates";
import { Section } from "components/section";
import { Button } from "ui/button";
import { DecorativeBackgroundImage } from "components/images";
import { Diptych } from "ui/diptych";
import { TextTitle } from "ui/text";

const featuredCaseStudySlugs = ["m1finance"];

const AwsRedshift: NextPage<LandingPageProps> = ({ page, announcementBanner, caseStudies }) => {
  const warehouseConfig = WarehouseCdpVariants.redshift;
  // Ensure correct order of case studies
  const caseStudy = caseStudies[0];

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
      theme="dark"
      bgColor={`new.${warehouseConfig.color}.900`}
      footerOptions={{ variant: warehouseConfig.color }}
    >
      <WarehouseCdpProvider warehouse={warehouseConfig}>
        <WarehouseHero theme="dark" title={page.title} description={page.description} />
        <WarehouseCustomers customers={page.customers} />
        <WarehouseAsCdp />

        {/** TODO: swap for WarehouseCaseStudies component if/when we get another Redshift customer story. */}
        <Section>
          <TextTitle align="center" size="lg" title={`${warehouseConfig.name} and Hightouch in action`} />

          <Diptych
            size="lg"
            direction="right"
            mt={{ base: "bigger", md: "huge" }}
            title={caseStudy.title}
            titleSize="sm"
            actions={
              <Button hasRightChevron href={`/customers/${caseStudy.customer.slug}`}>
                Read the story
              </Button>
            }
            media={
              <DecorativeBackgroundImage
                src={caseStudy.fullImageUrl}
                style={{ objectFit: "cover", objectPosition: "right" }}
              />
            }
          />
        </Section>

        <SolidPlate
          theme="dark"
          bgColor={`new.${warehouseConfig.color}.950`}
          spotlightColor={`new.${warehouseConfig.color}.900`}
        >
          <WarehouseCdpDiagram theme="dark" />
          <WarehouseAccordion theme="dark" />
          <WarehouseSecurityFeatures theme="dark" bgColor={`new.${warehouseConfig.color}.950`} />
        </SolidPlate>
        <WarehouseWordCloud />
        <DataTeamsCards warehouse={warehouseConfig} />
        <WarehouseUseCaseTabs />
        <WarehouseFaq />
      </WarehouseCdpProvider>
    </Page>
  );
};

export const getStaticProps = getLandingPageProps("/solutions/aws-redshift-cdp", {
  loadCaseStudies: featuredCaseStudySlugs,
});

export default AwsRedshift;
